/**
 * Medium-mode living afternoon: partly cloudy, breezy meadow.
 * Stronger wind than easy sunrise — waves of grass, drifting clouds
 * that mute the light, particles carried left with the breeze.
 * Decorative only; gameplay wind push lives in weatherFx / homeSketch.
 */

const SAGE = [
  [105, 138, 95],
  [88, 120, 82],
  [125, 150, 108],
  [95, 128, 90],
];
const BLOOM = [
  [210, 165, 155],
  [220, 195, 150],
  [185, 180, 195],
  [215, 185, 165],
  [170, 185, 160],
];
const SEED = [
  [230, 215, 180],
  [200, 190, 160],
  [180, 175, 150],
];

function rand(a, b) {
  return a + Math.random() * (b - a);
}

function pick(arr) {
  return arr[Math.floor(Math.random() * arr.length)];
}

function n01(p, x, y, z) {
  return p.noise(x, y, z);
}

function sway(p, seed, t, amp, speed) {
  return (n01(p, seed * 0.17, t * speed, seed * 0.03) - 0.5) * 2 * amp;
}

function parallax(mx, my, depth, boardW, boardH) {
  const nx = (mx / Math.max(1, boardW) - 0.5) * 2;
  const ny = (my / Math.max(1, boardH) - 0.5) * 2;
  return { x: nx * depth * 16, y: ny * depth * 9 };
}

export function clearBreezy(state) {
  state.breezy = null;
}

export function seedBreezy(state, boardW, boardH) {
  const clouds = [];
  for (let i = 0; i < 7; i += 1) {
    clouds.push({
      x: rand(-0.1, 1.1),
      y: rand(0.04, 0.32),
      w: rand(0.18, 0.34),
      h: rand(0.08, 0.16),
      speed: rand(0.00035, 0.0007),
      alpha: rand(120, 190),
      seed: rand(0, 100),
      // How much this cloud dims the sun when overlapping
      shade: rand(0.35, 0.85),
    });
  }

  const grasses = [];
  for (let i = 0; i < 22; i += 1) {
    grasses.push({
      x: rand(0.02, 0.98),
      baseY: rand(0.68, 0.84),
      h: rand(0.09, 0.17),
      depth: rand(0.18, 0.4),
      seed: rand(0, 100),
      amp: rand(4, 9),
      speed: rand(0.0009, 0.0016),
      color: pick(SAGE),
      thick: rand(1.2, 2),
    });
  }
  for (let i = 0; i < 32; i += 1) {
    grasses.push({
      x: rand(0.01, 0.99),
      baseY: rand(0.76, 0.93),
      h: rand(0.13, 0.26),
      depth: rand(0.42, 0.72),
      seed: rand(0, 100),
      amp: rand(8, 16),
      speed: rand(0.0011, 0.002),
      color: pick(SAGE),
      thick: rand(1.5, 2.6),
    });
  }
  for (let i = 0; i < 26; i += 1) {
    grasses.push({
      x: rand(0, 1),
      baseY: rand(0.86, 1.02),
      h: rand(0.18, 0.34),
      depth: rand(0.75, 1),
      seed: rand(0, 100),
      amp: rand(12, 24),
      speed: rand(0.0014, 0.0026),
      color: pick(SAGE),
      thick: rand(1.8, 3.2),
    });
  }
  grasses.sort((a, b) => a.depth - b.depth);

  const blooms = [];
  for (let i = 0; i < 14; i += 1) {
    blooms.push({
      x: rand(0.05, 0.95),
      y: rand(0.58, 0.8),
      r: rand(3, 6),
      depth: rand(0.2, 0.45),
      seed: rand(0, 100),
      amp: rand(3, 7),
      speed: rand(0.0008, 0.0014),
      color: pick(BLOOM),
      alpha: rand(90, 140),
      petals: 4 + Math.floor(Math.random() * 2),
    });
  }
  for (let i = 0; i < 16; i += 1) {
    blooms.push({
      x: rand(0.03, 0.97),
      y: rand(0.68, 0.92),
      r: rand(4.5, 8),
      depth: rand(0.5, 0.85),
      seed: rand(0, 100),
      amp: rand(6, 14),
      speed: rand(0.001, 0.0018),
      color: pick(BLOOM),
      alpha: rand(120, 175),
      petals: 5,
    });
  }
  for (let i = 0; i < 8; i += 1) {
    blooms.push({
      // Soft shrubs / foliage clumps
      kind: 'shrub',
      x: rand(0.04, 0.96),
      y: rand(0.72, 0.92),
      r: rand(10, 18),
      depth: rand(0.55, 0.9),
      seed: rand(0, 100),
      amp: rand(5, 11),
      speed: rand(0.0009, 0.0015),
      color: pick(SAGE),
      alpha: rand(90, 140),
    });
  }
  blooms.sort((a, b) => a.depth - b.depth);

  const particles = [];
  for (let i = 0; i < 22; i += 1) {
    particles.push({
      kind: 'pollen',
      x: rand(0, 1),
      y: rand(0.1, 0.9),
      r: rand(1.2, 2.6),
      depth: rand(0.35, 0.95),
      seed: rand(0, 100),
      speed: rand(0.0012, 0.0028),
      alpha: rand(50, 100),
    });
  }
  for (let i = 0; i < 12; i += 1) {
    particles.push({
      kind: 'seed',
      x: rand(0, 1),
      y: rand(0.15, 0.85),
      r: rand(2.5, 5),
      depth: rand(0.4, 1),
      seed: rand(0, 100),
      speed: rand(0.0015, 0.0032),
      alpha: rand(70, 130),
      color: pick(SEED),
      spin: rand(0, Math.PI * 2),
    });
  }
  for (let i = 0; i < 8; i += 1) {
    particles.push({
      kind: 'leaf',
      x: rand(0, 1),
      y: rand(0.2, 0.8),
      r: rand(4, 7),
      depth: rand(0.5, 1),
      seed: rand(0, 100),
      speed: rand(0.0018, 0.0035),
      alpha: rand(90, 140),
      color: pick(SAGE),
      spin: rand(0, Math.PI * 2),
    });
  }

  const lightPatches = [];
  for (let i = 0; i < 4; i += 1) {
    lightPatches.push({
      x: rand(0.1, 0.9),
      y: rand(0.55, 0.9),
      w: rand(0.14, 0.28),
      h: rand(0.08, 0.16),
      seed: rand(0, 100),
      phase: rand(0, Math.PI * 2),
    });
  }

  const sun = { xFrac: 0.72, yFrac: 0.14 };

  state.breezy = {
    w: boardW,
    h: boardH,
    clouds,
    grasses,
    blooms,
    particles,
    lightPatches,
    sun,
    sunOcclusion: 0,
  };
}

function drawSky(p, breezy, boardW, boardH, t, reducedMotion, cool) {
  p.push();
  p.noStroke();
  // Afternoon sky — warmer when clear, cooler when occluded
  for (let i = 0; i < 8; i += 1) {
    const y0 = (boardH * i) / 8;
    const y1 = (boardH * (i + 1)) / 8 + 1;
    const k = i / 7;
    const warmR = 210 - k * 25;
    const warmG = 220 - k * 15;
    const warmB = 235 - k * 10;
    const coolR = 170 - k * 20;
    const coolG = 185 - k * 10;
    const coolB = 205;
    const r = warmR + (coolR - warmR) * cool;
    const g = warmG + (coolG - warmG) * cool;
    const b = warmB + (coolB - warmB) * cool;
    p.fill(r, g, b, 52);
    p.rect(0, y0, boardW, y1 - y0);
  }

  const sx = boardW * breezy.sun.xFrac;
  const sy = boardH * breezy.sun.yFrac;
  const glow = (1 - cool * 0.7) * (reducedMotion ? 1 : 0.92 + n01(p, t * 0.0003, 2, 3) * 0.12);
  p.fill(255, 220, 140, Math.floor(36 * glow));
  p.ellipse(sx, sy, boardH * 0.42, boardH * 0.36);
  p.fill(255, 230, 160, Math.floor(55 * glow));
  p.ellipse(sx, sy, boardH * 0.2, boardH * 0.18);
  p.fill(255, 245, 200, Math.floor(70 * glow));
  p.ellipse(sx, sy, boardH * 0.09, boardH * 0.08);

  // Soft god-rays when partly clear
  if (cool < 0.65) {
    const rayA = Math.floor(14 * (1 - cool));
    for (let i = 0; i < 5; i += 1) {
      const ang = -0.15 + i * 0.22 + (reducedMotion ? 0 : sway(p, i + 2, t, 0.03, 0.00025));
      p.push();
      p.translate(sx, sy);
      p.rotate(ang);
      p.fill(255, 230, 170, rayA);
      p.beginShape();
      p.vertex(0, 0);
      p.vertex(boardW * 0.04, boardH * 0.95);
      p.vertex(-boardW * 0.04, boardH * 0.95);
      p.endShape(p.CLOSE);
      p.pop();
    }
  }
  p.pop();
  return { sx, sy };
}

function updateAndDrawClouds(p, breezy, boardW, boardH, t, reducedMotion, sun) {
  let occlusion = 0;
  p.push();
  p.noStroke();
  for (let i = 0; i < breezy.clouds.length; i += 1) {
    const c = breezy.clouds[i];
    if (!reducedMotion) {
      c.x -= c.speed;
      c.y += sway(p, c.seed, t, 0.0008, 0.0004);
      if (c.x + c.w < -0.15) c.x = 1.1;
    }
    const cx = boardW * c.x;
    const cy = boardH * c.y;
    const cw = boardW * c.w;
    const ch = boardH * c.h;

    // Soft layered cloud body
    p.fill(245, 248, 252, c.alpha * 0.45);
    p.ellipse(cx + 4, cy + 5, cw * 1.05, ch * 1.1);
    p.fill(255, 252, 248, c.alpha);
    p.ellipse(cx, cy, cw, ch);
    p.ellipse(cx - cw * 0.28, cy + ch * 0.1, cw * 0.55, ch * 0.75);
    p.ellipse(cx + cw * 0.3, cy + ch * 0.08, cw * 0.5, ch * 0.7);
    p.ellipse(cx + cw * 0.05, cy - ch * 0.22, cw * 0.48, ch * 0.65);
    p.fill(200, 210, 220, c.alpha * 0.35);
    p.ellipse(cx + cw * 0.1, cy + ch * 0.15, cw * 0.6, ch * 0.45);

    // Sun occlusion when cloud covers sun
    const dx = Math.abs(cx - sun.sx) / Math.max(1, cw * 0.55);
    const dy = Math.abs(cy - sun.sy) / Math.max(1, ch * 0.7);
    if (dx < 1.1 && dy < 1.2) {
      const cover = (1 - dx / 1.1) * (1 - dy / 1.2) * c.shade;
      occlusion = Math.max(occlusion, cover);
    }
  }
  p.pop();
  breezy.sunOcclusion = Math.min(1, occlusion);
  return breezy.sunOcclusion;
}

function drawHills(p, boardW, boardH, t, reducedMotion, cool, px) {
  p.push();
  p.noStroke();
  const drift = reducedMotion ? 0 : sway(p, 4.2, t, 8, 0.0003) + px * 0.2;
  const green = 150 - cool * 20;
  p.fill(green, 165 - cool * 15, 140, 48);
  p.beginShape();
  p.vertex(-20 + drift, boardH);
  for (let i = 0; i <= 14; i += 1) {
    const x = (boardW * i) / 14 + drift;
    const y =
      boardH * 0.58 +
      n01(p, i * 0.32, t * 0.00018, 7) * boardH * 0.1;
    p.vertex(x, y);
  }
  p.vertex(boardW + 20 + drift, boardH);
  p.endShape(p.CLOSE);

  const drift2 = reducedMotion ? 0 : sway(p, 8.1, t, 10, 0.00035) + px * 0.35;
  p.fill(130 - cool * 15, 150 - cool * 12, 120, 55);
  p.beginShape();
  p.vertex(-20 + drift2, boardH);
  for (let i = 0; i <= 16; i += 1) {
    const x = (boardW * i) / 16 + drift2;
    const y =
      boardH * 0.66 +
      n01(p, i * 0.38 + 3, t * 0.00022, 8) * boardH * 0.12;
    p.vertex(x, y);
  }
  p.vertex(boardW + 20 + drift2, boardH);
  p.endShape(p.CLOSE);
  p.pop();
}

function drawLightPatches(p, breezy, boardW, boardH, t, cool, gust) {
  p.push();
  p.noStroke();
  const strength = (1 - cool) * (0.55 + gust * 0.15);
  for (let i = 0; i < breezy.lightPatches.length; i += 1) {
    const lp = breezy.lightPatches[i];
    const life =
      0.45 +
      0.55 *
        (0.5 +
          0.5 * Math.sin(t * 0.0012 + lp.phase) *
            (0.7 + n01(p, lp.seed, t * 0.00035, 6) * 0.3));
    const a = Math.floor(28 * strength * life);
    // Drift left slowly with wind
    const dx = -((t * 0.08 + lp.seed * 10) % (boardW * 0.4));
    p.fill(255, 235, 180, a);
    p.ellipse(
      boardW * lp.x + dx,
      boardH * lp.y,
      boardW * lp.w,
      boardH * lp.h
    );
    // Cool cloud shadow counterpart
    p.fill(90, 110, 130, Math.floor(16 * cool * life));
    p.ellipse(
      boardW * ((lp.x + 0.35) % 1),
      boardH * lp.y + 8,
      boardW * lp.w * 1.1,
      boardH * lp.h * 0.9
    );
  }
  p.pop();
}

function drawGrassBlade(p, g, boardW, boardH, t, reducedMotion, mx, my, gust) {
  const par = parallax(mx, my, g.depth, boardW, boardH);
  const baseX = boardW * g.x + par.x;
  const baseY = boardH * g.baseY + par.y * 0.35;
  const h = boardH * g.h;
  // Wind leans left; gusts amplify
  const windLean = -(6 + g.depth * 10 + gust * 18);
  const tipSway = reducedMotion
    ? windLean * 0.3
    : windLean + sway(p, g.seed, t, g.amp * (1 + gust * 0.8), g.speed);
  // Traveling wave across meadow (gust ripple)
  const wave =
    reducedMotion || !gust
      ? 0
      : Math.sin(g.x * 14 - t * 0.08 + g.seed) * gust * 6 * g.depth;
  const midSway = (tipSway + wave) * 0.45;
  const [r, gr, b] = g.color;
  const alpha = Math.floor(75 + g.depth * 95);

  p.push();
  p.noFill();
  p.stroke(r, gr, b, alpha);
  p.strokeWeight(g.thick);
  p.bezier(
    baseX,
    baseY,
    baseX + midSway * 0.35,
    baseY - h * 0.4,
    baseX + midSway,
    baseY - h * 0.72,
    baseX + tipSway + wave,
    baseY - h
  );
  p.pop();
}

function drawBloom(p, f, boardW, boardH, t, reducedMotion, mx, my, gust) {
  const par = parallax(mx, my, f.depth, boardW, boardH);
  const wind = -(3 + f.depth * 8 + gust * 12);
  const swayX = reducedMotion
    ? wind * 0.25
    : wind + sway(p, f.seed, t, f.amp * (1 + gust * 0.6), f.speed);
  const swayY = reducedMotion
    ? 0
    : sway(p, f.seed + 3, t, f.amp * 0.3, f.speed * 0.9);
  const x = boardW * f.x + swayX + par.x;
  const y = boardH * f.y + swayY + par.y;

  if (f.kind === 'shrub') {
    const [r, g, b] = f.color;
    p.push();
    p.noStroke();
    p.fill(r, g, b, f.alpha);
    p.ellipse(x, y, f.r * 1.6, f.r);
    p.ellipse(x - f.r * 0.4, y + 2, f.r * 1.1, f.r * 0.75);
    p.ellipse(x + f.r * 0.35, y + 1, f.r * 1.0, f.r * 0.7);
    p.fill(r + 15, g + 20, b + 10, f.alpha * 0.55);
    p.ellipse(x, y - f.r * 0.15, f.r * 0.9, f.r * 0.55);
    p.pop();
    return;
  }

  const [r, g, b] = f.color;
  const a = Math.floor(f.alpha);
  p.push();
  p.noStroke();
  if (f.depth > 0.45) {
    p.stroke(100, 130, 90, Math.floor(a * 0.4));
    p.strokeWeight(1.3);
    p.line(x - swayX * 0.25, y + f.r * 1.7, x, y);
    p.noStroke();
  }
  for (let i = 0; i < f.petals; i += 1) {
    const ang = (Math.PI * 2 * i) / f.petals + f.seed;
    p.fill(r, g, b, a);
    p.circle(
      x + Math.cos(ang) * f.r * 0.55,
      y + Math.sin(ang) * f.r * 0.55,
      f.r
    );
  }
  p.fill(240, 225, 170, Math.floor(a * 0.85));
  p.circle(x, y, f.r * 0.7);
  p.pop();
}

function drawParticles(p, breezy, boardW, boardH, t, reducedMotion, mx, my, gust) {
  p.push();
  p.noStroke();
  for (let i = 0; i < breezy.particles.length; i += 1) {
    const pt = breezy.particles[i];
    if (!reducedMotion) {
      // Carry left with the breeze; gusts speed them up
      const wind = pt.speed * (1.1 + gust * 1.4);
      pt.x -= wind;
      pt.y +=
        (n01(p, pt.seed, t * 0.0004, 2.2) - 0.5) * 0.0012 +
        Math.sin(t * 0.02 + pt.seed) * 0.00015;
      if (pt.x < -0.08) {
        pt.x = 1.08;
        pt.y = rand(0.1, 0.9);
      }
      if (pt.kind !== 'pollen') pt.spin += 0.02 + pt.depth * 0.015 + gust * 0.02;
    }
    const par = parallax(mx, my, pt.depth, boardW, boardH);
    const x = boardW * pt.x + par.x;
    const y = boardH * pt.y + par.y;

    if (pt.kind === 'leaf') {
      const [r, g, b] = pt.color;
      p.push();
      p.translate(x, y);
      p.rotate(pt.spin || 0);
      p.fill(r, g, b, pt.alpha);
      p.ellipse(0, 0, pt.r * 1.5, pt.r * 0.65);
      p.pop();
    } else if (pt.kind === 'seed') {
      const [r, g, b] = pt.color;
      p.push();
      p.translate(x, y);
      p.rotate(pt.spin || 0);
      p.fill(r, g, b, pt.alpha);
      p.ellipse(0, 0, pt.r * 1.3, pt.r * 0.45);
      p.stroke(r, g, b, pt.alpha * 0.7);
      p.strokeWeight(1);
      p.line(0, 0, pt.r * 0.9, -pt.r * 0.3);
      p.noStroke();
      p.pop();
    } else {
      p.fill(255, 245, 220, pt.alpha);
      p.circle(x, y, pt.r);
    }
  }
  p.pop();
}

function drawDistantBirds(p, boardW, boardH, t, reducedMotion, cool) {
  if (reducedMotion) return;
  p.push();
  p.noFill();
  p.stroke(90, 100, 110, Math.floor(50 + (1 - cool) * 30));
  p.strokeWeight(1.4);
  for (let i = 0; i < 3; i += 1) {
    const x = ((t * (0.35 + i * 0.12) + i * 180) % (boardW + 80)) - 40;
    // Drift left overall
    const bx = boardW - x;
    const by = boardH * (0.18 + i * 0.06) + Math.sin(t * 0.03 + i) * 4;
    const flap = Math.sin(t * 0.12 + i * 2) * 4;
    p.arc(bx, by, 10, 6 + flap, Math.PI + 0.2, -0.2);
    p.arc(bx + 8, by, 10, 6 - flap, Math.PI + 0.2, -0.2);
  }
  p.pop();
}

/**
 * @param {number} gustStrength 0..1 from active windLines / ambient gust
 */
export function drawBreezy(
  p,
  state,
  boardW,
  boardH,
  t,
  reducedMotion,
  mouseX,
  mouseY,
  gustStrength
) {
  if (!state.breezy) return;
  const breezy = state.breezy;
  if (Math.abs(breezy.w - boardW) > 48 || Math.abs(breezy.h - boardH) > 36) {
    seedBreezy(state, boardW, boardH);
  }

  const mx =
    mouseX == null || mouseX < 0 || mouseX > boardW ? boardW * 0.5 : mouseX;
  const my =
    mouseY == null || mouseY < 0 || mouseY > boardH ? boardH * 0.4 : mouseY;
  const farPar = parallax(mx, my, 0.12, boardW, boardH);
  const gust = Math.max(0, Math.min(1, gustStrength || 0));

  // Approximate cool from prior frame occlusion, then refine
  let cool = breezy.sunOcclusion || 0;
  const sun = drawSky(p, breezy, boardW, boardH, t, reducedMotion, cool);
  cool = updateAndDrawClouds(p, breezy, boardW, boardH, t, reducedMotion, sun);
  // Soft cool wash when clouds block the sun
  if (cool > 0.05) {
    p.push();
    p.noStroke();
    p.fill(120, 140, 165, Math.floor(38 * cool));
    p.rect(0, 0, boardW, boardH);
    p.pop();
  }

  drawHills(p, boardW, boardH, t, reducedMotion, cool, farPar.x);
  drawDistantBirds(p, boardW, boardH, t, reducedMotion, cool);
  drawLightPatches(p, breezy, boardW, boardH, t, cool, gust);

  const grasses = breezy.grasses;
  const blooms = breezy.blooms;

  for (let i = 0; i < grasses.length; i += 1) {
    if (grasses[i].depth < 0.5) {
      drawGrassBlade(p, grasses[i], boardW, boardH, t, reducedMotion, mx, my, gust);
    }
  }
  for (let i = 0; i < blooms.length; i += 1) {
    if (blooms[i].depth < 0.5) {
      drawBloom(p, blooms[i], boardW, boardH, t, reducedMotion, mx, my, gust);
    }
  }
  for (let i = 0; i < grasses.length; i += 1) {
    if (grasses[i].depth >= 0.5 && grasses[i].depth < 0.78) {
      drawGrassBlade(p, grasses[i], boardW, boardH, t, reducedMotion, mx, my, gust);
    }
  }
  for (let i = 0; i < blooms.length; i += 1) {
    if (blooms[i].depth >= 0.5 && blooms[i].depth < 0.78) {
      drawBloom(p, blooms[i], boardW, boardH, t, reducedMotion, mx, my, gust);
    }
  }

  drawParticles(p, breezy, boardW, boardH, t, reducedMotion, mx, my, gust);

  for (let i = 0; i < grasses.length; i += 1) {
    if (grasses[i].depth >= 0.78) {
      drawGrassBlade(p, grasses[i], boardW, boardH, t, reducedMotion, mx, my, gust);
    }
  }
  for (let i = 0; i < blooms.length; i += 1) {
    if (blooms[i].depth >= 0.78) {
      drawBloom(p, blooms[i], boardW, boardH, t, reducedMotion, mx, my, gust);
    }
  }
}
